import React, { useEffect, useState } from "react";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import useApi from "../../../core/customhook/useApi";
import Loader from "../../../core/common/selfLoder";

export interface EmployeeSummary {
  total_employees: number;
  active_employees: string;
  inactive_employees: string;
  new_joinings: string;
}
export interface RawEmployee {
  employee_id: string;
  employee_name: string;
  email_id: string;
  designation: string;
  department: string;
  work_location: string;
  status: string;
  photo: string;
}
export interface TableEmployee {
  EmpId: string;
  Name: string;
  Email: string;
  Designation: string;
  CurrentRole: string;
  Image: string;
  WorkLocation: string;
  Status: string;
}

const EmployeeDirectory = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState<TableEmployee[]>(
    []
  );
  const [totalemp, setTotalemp] = useState<number>();

  const [searchSubmitted, setSearchSubmitted] = useState(false);

  const {
    data: searchData,
    callApi: searchEmployees,
    loading: searchLoading,
  } = useApi<{
    success: boolean;
    total: number;
    data: RawEmployee[];
  }>("/api/search-employee-directory", {
    method: "POST",
    autoCall: false,
  });

  const handleSearch =
    // debounce<any>(
    async (value: string) => {
      setSearchText(value);
      setSearchSubmitted(true);

      if (value.trim()) {
        try {
          const response = await searchEmployees({ search: value });

          if (response?.success && Array.isArray(response.data)) {
            const searchResults: TableEmployee[] = response.data.map(
              (emp, index) => ({
                EmpId: emp.employee_id,
                Name: emp.employee_name,
                Email: emp.email_id,
                Designation: emp.designation,
                CurrentRole: emp.department,
                Image: emp.photo,
                WorkLocation: emp.work_location,
                Status: emp.status,
                Sno: index + 1,
              })
            );

            setFilteredEmployees(searchResults);
            setTotalemp(response.total ?? searchResults.length);
          } else {
            setFilteredEmployees([]);
            setTotalemp(0);
          }
        } catch (error) {
          console.error("Search API failed", error);
          setFilteredEmployees([]);
          setTotalemp(0);
        }
      } else {
        setFilteredEmployees([]);
        setTotalemp(employeeData?.data?.total ?? 0);
      }
    };
  // , 500);

  const { data: summaryData, loading: summaryLoading } = useApi<{
    success: boolean;
    data: EmployeeSummary;
  }>("/api/employee-summary", {
    method: "GET",
    autoCall: true,
  });

  const { data: employeeData, loading: employeeLoading } = useApi<{
    success: boolean;
    data: { total: number; employees: RawEmployee[] };
  }>("/api/get-employee-directory", {
    method: "POST",
    payload: { page: currentPage, limit },
    autoCall: true,
    dependencies: [currentPage, limit],
  });

  useEffect(() => {
    setTotalemp(employeeData?.data?.total);
  }, []);

  const transformedEmployees: TableEmployee[] =
    employeeData?.data?.employees.map((emp, index) => ({
      EmpId: emp.employee_id,
      Name: emp.employee_name,
      Email: emp.email_id,
      Designation: emp.designation,
      CurrentRole: emp.department,
      Image: emp.photo,
      WorkLocation: emp.work_location,
      Status: emp.status,
      Sno: index + 1,
    })) ?? [];

  const columns = [
    {
      title: "SNo.",
      dataIndex: "Sno",
    },
    {
      title: "Emp ID",
      dataIndex: "EmpId",
      render: (text: string, record: any) => (
        <Link to={`/employee-details/${record.EmpId}`}>{text}</Link>
      ),
      sorter: (a: any, b: any) => a.EmpId.localeCompare(b.EmpId),
    },
    {
      title: "Name",
      dataIndex: "Name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <div
            className="avatar avatar-md overflow-hidden rounded-circle"
            style={{
              width: "32px",
              height: "32px",
              minWidth: "32px",
            }}
          >
            <ImageWithBasePath
              src={
                record.Image ||
                "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQABqQIdskCD9BK0I81EbVfV9tTz320XvJ35A&s"
              }
              alt="img"
              className="img-fluid w-100 h-100 object-fit-cover"
              width={32}
              height={32}
            />
          </div>
          <div className="ms-2">
            <p className="text-dark mb-0">{record.Name}</p>
            <span className="fs-12">{record.CurrentRole}</span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.Name.length - b.Name.length,
    },
    {
      title: "Email",
      dataIndex: "Email",
      sorter: (a: any, b: any) => a.Email.length - b.Email.length,
    },

    {
      title: "Designation",
      dataIndex: "Designation",
      render: (text: string, record: any) => (
        <div className="dropdown me-3">{text}</div>
      ),
      sorter: (a: any, b: any) => a.Designation.length - b.Designation.length,
    },
    {
      title: "Department",
      dataIndex: "CurrentRole",
      sorter: (a: any, b: any) => a.CurrentRole.localeCompare(b.CurrentRole),
    },
    {
      title: "Work Location",
      dataIndex: "WorkLocation",
      sorter: (a: any, b: any) => a.WorkLocation.localeCompare(b.WorkLocation),
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: string) => {
        const isActive = text === "1" || text?.toLowerCase?.() === "1";
        return (
          <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
            {isActive ? "Active" : "Exit"}
          </span>
        );
      },
      sorter: (a: any, b: any) =>
        (a.Status || "").localeCompare(b.Status || ""),
    },
  ];
  return (
    <>
      <Loader loading={summaryLoading || employeeLoading} />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Employee Directory </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.employeeDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Employee Resources</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Employee Directory
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>

          <div className="card mb-3 px-1 lg:px-4">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-auto">
                  <label htmlFor="employeeName" className="col-form-label mb-0">
                    Employee Name:
                  </label>
                </div>
                <div className="col-md-6 position-relative">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault(); // Prevent page reload
                      handleSearch(searchText); // Call your search function
                    }}
                    className="d-flex align-items-center"
                  >
                    <input
                      type="text"
                      id="employeeName"
                      className="form-control pe-5"
                      placeholder="Enter name to search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    {searchText && (
                      <img
                        src="../assets/img/icon/close.png"
                        alt="Clear"
                        onClick={() => {
                          setSearchText("");
                          setFilteredEmployees([]);
                          setSearchSubmitted(false);
                        }}
                        style={{
                          position: "absolute",
                          right: "98px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                          opacity: 0.7,
                        }}
                      />
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary custom_button ms-2 mt-0"
                    >
                      Search
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* THIS SECITON ONLY SHOW WHEN SERACH INPUT HAVE DATA  */}
          {searchSubmitted && (
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h5>Employee List</h5>
              </div>
              <div className="card-body p-0">
                <Table
                  dataSource={
                    filteredEmployees.length > 0
                      ? filteredEmployees
                      : transformedEmployees
                  }
                  columns={columns}
                  Selection={false}
                  totalEmployees={
                    filteredEmployees.length > 0
                      ? totalemp ?? filteredEmployees.length
                      : employeeData?.data?.total ?? 0
                  }
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  limit={limit}
                  setLimit={setLimit}
                  onSearch={handleSearch}
                  searchText={searchText}
                  showSearchInput={false}
                />
              </div>
            </div>
          )}
        </div>
        <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
          <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default EmployeeDirectory;

// OLD CODE 29-07-2025
// import React, { useEffect, useState } from "react";
// import { all_routes } from "../../router/all_routes";
// import { Link } from "react-router-dom";
// import Table from "../../../core/common/dataTable/index";
// import ImageWithBasePath from "../../../core/common/imageWithBasePath";
// import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
// import useApi from "../../../core/customhook/useApi";
// import Loader from "../../../core/common/selfLoder";
// import axios from "axios";
// import { jwttoken } from "../../../environment";
// import debounce from "lodash.debounce";

// export interface EmployeeSummary {
//   total_employees: number;
//   active_employees: string;
//   inactive_employees: string;
//   new_joinings: string;
// }
// export interface RawEmployee {
//   employee_id: string;
//   employee_name: string;
//   email_id: string;
//   designation: string;
//   department: string;
//   work_location: string;
//   status: string;
//   photo: string;
// }
// export interface TableEmployee {
//   EmpId: string;
//   Name: string;
//   Email: string;
//   Designation: string;
//   CurrentRole: string;
//   Image: string;
//   WorkLocation: string;
//   Status: string;
// }

// const EmployeeDirectory = () => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [limit, setLimit] = useState(10);
//   const [searchText, setSearchText] = useState("");
//   const [filteredEmployees, setFilteredEmployees] = useState<TableEmployee[]>(
//     []
//   );
//   const [totalemp, setTotalemp] = useState<number>();

//   const {
//     data: searchData,
//     callApi: searchEmployees,
//     loading: searchLoading,
//   } = useApi<{
//     success: boolean;
//     total: number;
//     data: RawEmployee[];
//   }>("/api/search-employee-directory", {
//     method: "POST",
//     autoCall: false,
//   });

//   const handleSearch =
//     // debounce<any>(
//     async (value: string) => {
//       setSearchText(value);

//       if (value.trim()) {
//         try {
//           const response = await searchEmployees({ search: value });

//           if (response?.success && Array.isArray(response.data)) {
//             const searchResults: TableEmployee[] = response.data.map(
//               (emp, index) => ({
//                 EmpId: emp.employee_id,
//                 Name: emp.employee_name,
//                 Email: emp.email_id,
//                 Designation: emp.designation,
//                 CurrentRole: emp.department,
//                 Image: emp.photo,
//                 WorkLocation: emp.work_location,
//                 Status: emp.status,
//                 Sno: index + 1,
//               })
//             );

//             setFilteredEmployees(searchResults);
//             setTotalemp(response.total ?? searchResults.length);
//           } else {
//             setFilteredEmployees([]);
//             setTotalemp(0);
//           }
//         } catch (error) {
//           console.error("Search API failed", error);
//           setFilteredEmployees([]);
//           setTotalemp(0);
//         }
//       } else {
//         setFilteredEmployees([]);
//         setTotalemp(employeeData?.data?.total ?? 0);
//       }
//     };
//   // , 500);

//   const { data: summaryData, loading: summaryLoading } = useApi<{
//     success: boolean;
//     data: EmployeeSummary;
//   }>("/api/employee-summary", {
//     method: "GET",
//     autoCall: true,
//   });

//   const { data: employeeData, loading: employeeLoading } = useApi<{
//     success: boolean;
//     data: { total: number; employees: RawEmployee[] };
//   }>("/api/get-employee-directory", {
//     method: "POST",
//     payload: { page: currentPage, limit },
//     autoCall: true,
//     dependencies: [currentPage, limit],
//   });

//   useEffect(() => {
//     setTotalemp(employeeData?.data?.total);
//   }, []);

//   const transformedEmployees: TableEmployee[] =
//     employeeData?.data?.employees.map((emp, index) => ({
//       EmpId: emp.employee_id,
//       Name: emp.employee_name,
//       Email: emp.email_id,
//       Designation: emp.designation,
//       CurrentRole: emp.department,
//       Image: emp.photo,
//       WorkLocation: emp.work_location,
//       Status: emp.status,
//       Sno: index + 1,
//     })) ?? [];

//   const columns = [
//     {
//       title: "SNo.",
//       dataIndex: "Sno",
//     },
//     {
//       title: "Emp ID",
//       dataIndex: "EmpId",
//       render: (text: string, record: any) => (
//         <Link to={`/employee-details/${record.EmpId}`}>{text}</Link>
//       ),
//       sorter: (a: any, b: any) => a.EmpId.localeCompare(b.EmpId),
//     },
//     {
//       title: "Name",
//       dataIndex: "Name",
//       render: (text: string, record: any) => (
//         <div className="d-flex align-items-center">
//           <div
//             className="avatar avatar-md overflow-hidden rounded-circle"
//             style={{
//               width: "32px",
//               height: "32px",
//               minWidth: "32px",
//             }}
//           >
//             <ImageWithBasePath
//               src={
//                 record.Image ||
//                 "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQABqQIdskCD9BK0I81EbVfV9tTz320XvJ35A&s"
//               }
//               alt="img"
//               className="img-fluid w-100 h-100 object-fit-cover"
//               width={32}
//               height={32}
//             />
//           </div>
//           <div className="ms-2">
//             <p className="text-dark mb-0">{record.Name}</p>
//             <span className="fs-12">{record.CurrentRole}</span>
//           </div>
//         </div>
//       ),
//       sorter: (a: any, b: any) => a.Name.length - b.Name.length,
//     },
//     {
//       title: "Email",
//       dataIndex: "Email",
//       sorter: (a: any, b: any) => a.Email.length - b.Email.length,
//     },

//     {
//       title: "Designation",
//       dataIndex: "Designation",
//       render: (text: string, record: any) => (
//         <div className="dropdown me-3">{text}</div>
//       ),
//       sorter: (a: any, b: any) => a.Designation.length - b.Designation.length,
//     },
//     {
//       title: "Department",
//       dataIndex: "CurrentRole",
//       sorter: (a: any, b: any) => a.CurrentRole.localeCompare(b.CurrentRole),
//     },
//     {
//       title: "Work Location",
//       dataIndex: "WorkLocation",
//       sorter: (a: any, b: any) => a.WorkLocation.localeCompare(b.WorkLocation),
//     },
//     {
//       title: "Status",
//       dataIndex: "Status",
//       render: (text: string) => {
//         const isActive = text === "1" || text?.toLowerCase?.() === "1";
//         return (
//           <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
//             {isActive ? "Active" : "Exit"}
//           </span>
//         );
//       },
//       sorter: (a: any, b: any) =>
//         (a.Status || "").localeCompare(b.Status || ""),
//     },
//   ];
//   return (
//     <>
//       <Loader loading={summaryLoading || employeeLoading} />
//       {/* Page Wrapper */}
//       <div className="page-wrapper">
//         <div className="content">
//           {/* Breadcrumb */}
//           <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
//             <div className="my-auto mb-2">
//               <h2 className="mb-1">Employee Directory </h2>
//               <nav>
//                 <ol className="breadcrumb mb-0">
//                   <li className="breadcrumb-item">
//                     <Link to={all_routes.employeeDashboard}>
//                       <i className="ti ti-smart-home" />
//                     </Link>
//                   </li>
//                   <li className="breadcrumb-item">Employee Resources</li>
//                   <li className="breadcrumb-item active" aria-current="page">
//                     Employee Directory
//                   </li>
//                 </ol>
//               </nav>
//             </div>
//             <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
//               <div className="head-icons ms-2">
//                 <CollapseHeader />
//               </div>
//             </div>
//           </div>

// <div className="card mb-3 px-4">
//   <div className="card-body">
//     <div className="row g-3 align-items-center">
//       <div className="col-auto">
//         <label htmlFor="employeeName" className="col-form-label mb-0">
//           Employee Name:
//         </label>
//       </div>

//       <div className="col-md-6 position-relative">
//         <input
//           type="text"
//           id="employeeName"
//           className="form-control pe-5"
//           placeholder="Enter name to search"
//           value={searchText}
//           onChange={(e) => setSearchText(e.target.value)}
//         />
//         {searchText && (
//           <img
//             src="../assets/img/icon/close.png"
//             alt="Clear"
//             onClick={() => {
//               setSearchText("");
//               setFilteredEmployees([]);
//             }}
//             style={{
//               position: "absolute",
//               right: "15px",
//               top: "50%",
//               transform: "translateY(-50%)",
//               width: "16px",
//               height: "16px",
//               cursor: "pointer",
//               opacity: 0.7,
//             }}
//           />
//         )}
//       </div>

//       <div className="col-auto">
//         <button
//           className="btn btn-primary custom_button"
//           onClick={() => handleSearch(searchText)}
//         >
//           Search
//         </button>
//       </div>
//     </div>
//   </div>
// </div>

// {/* <div className="card mb-3">
//   <div className="card-body d-flex align-items-center gap-2 flex-wrap">
//     <label htmlFor="employeeName" className="form-label mb-0">
//       Employee Name:
//     </label>

//     <div style={{ position: "relative", maxWidth: "250px", width: "100%" }}>
//       <input
//         type="text"
//         id="employeeName"
//         className="form-control pe-4"
//         placeholder="Enter name to search"
//         value={searchText}
//         onChange={(e) => setSearchText(e.target.value)}
//       />
//       {searchText && (
//         <img
//           src="../assets/img/icon/close.png"
//           alt="Clear"
//           onClick={() => {
//             setSearchText("");
//             setFilteredEmployees([]);
//           }}
//           style={{
//             position: "absolute",
//             right: "10px",
//             top: "50%",
//             transform: "translateY(-50%)",
//             width: "14px",
//             height: "14px",
//             cursor: "pointer",
//             opacity: 0.8
//           }}
//         />
//       )}
//     </div>

//     <button
//       className="btn btn-primary custom_button"
//       onClick={() => handleSearch(searchText)}
//     >
//       Search
//     </button>
//   </div>
// </div> */}

//           <div className="card">
//             <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
//               <h5>Employee List</h5>
//             </div>
//             <div className="card-body p-0">
//               <Table
//                 dataSource={
//                   filteredEmployees.length > 0
//                     ? filteredEmployees
//                     : transformedEmployees
//                 }
//                 columns={columns}
//                 Selection={false}
//                 totalEmployees={
//                   filteredEmployees.length > 0
//                     ? totalemp ?? filteredEmployees.length
//                     : employeeData?.data?.total ?? 0
//                 }
//                 currentPage={currentPage}
//                 setCurrentPage={setCurrentPage}
//                 limit={limit}
//                 setLimit={setLimit}
//                 onSearch={handleSearch}
//                 searchText={searchText}
//               />
//             </div>
//           </div>
//         </div>
//         <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
//           <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
//         </div>
//       </div>
//       {/* /Page Wrapper */}
//     </>
//   );
// };

// export default EmployeeDirectory;

// OLD CODE

// import React, { useEffect, useState } from "react";
// import { all_routes } from "../../router/all_routes";
// import { Link } from "react-router-dom";
// import Table from "../../../core/common/dataTable/index";
// import ImageWithBasePath from "../../../core/common/imageWithBasePath";
// import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
// import useApi from "../../../core/customhook/useApi";
// import Loader from "../../../core/common/selfLoder";
// import axios from "axios";
// import { jwttoken } from "../../../environment";
// import debounce from "lodash.debounce";

// export interface EmployeeSummary {
//   total_employees: number;
//   active_employees: string;
//   inactive_employees: string;
//   new_joinings: string;
// }
// export interface RawEmployee {
//   employee_id: string;
//   employee_name: string;
//   email_id: string;
//   designation: string;
//   department: string;
//   work_location: string;
//   status: string;
//   photo: string;
// }
// export interface TableEmployee {
//   EmpId: string;
//   Name: string;
//   Email: string;
//   Designation: string;
//   CurrentRole: string;
//   Image: string;
//   WorkLocation: string;
//   Status: string;
// }

// const EmployeeDirectory = () => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [limit, setLimit] = useState(10);
//   const [searchText, setSearchText] = useState("");
//   const [filteredEmployees, setFilteredEmployees] = useState<TableEmployee[]>(
//     []
//   );
//   const [totalemp, setTotalemp] = useState<number>();

//   const {
//     data: searchData,
//     callApi: searchEmployees,
//     loading: searchLoading,
//   } = useApi<{
//     success: boolean;
//     total: number;
//     data: RawEmployee[];
//   }>("/api/search-employee-directory", {
//     method: "POST",
//     autoCall: false,
//   });

//   const handleSearch =
//     // debounce<any>(
//     async (value: string) => {
//       setSearchText(value);

//       if (value.trim()) {
//         try {
//           const response = await searchEmployees({ search: value });

//           if (response?.success && Array.isArray(response.data)) {
//             const searchResults: TableEmployee[] = response.data.map(
//               (emp, index) => ({
//                 EmpId: emp.employee_id,
//                 Name: emp.employee_name,
//                 Email: emp.email_id,
//                 Designation: emp.designation,
//                 CurrentRole: emp.department,
//                 Image: emp.photo,
//                 WorkLocation: emp.work_location,
//                 Status: emp.status,
//                 Sno: index + 1,
//               })
//             );

//             setFilteredEmployees(searchResults);
//             setTotalemp(response.total ?? searchResults.length);
//           } else {
//             setFilteredEmployees([]);
//             setTotalemp(0);
//           }
//         } catch (error) {
//           console.error("Search API failed", error);
//           setFilteredEmployees([]);
//           setTotalemp(0);
//         }
//       } else {
//         setFilteredEmployees([]);
//         setTotalemp(employeeData?.data?.total ?? 0);
//       }
//     };
//   // , 500);

//   const { data: summaryData, loading: summaryLoading } = useApi<{
//     success: boolean;
//     data: EmployeeSummary;
//   }>("/api/employee-summary", {
//     method: "GET",
//     autoCall: true,
//   });

//   const { data: employeeData, loading: employeeLoading } = useApi<{
//     success: boolean;
//     data: { total: number; employees: RawEmployee[] };
//   }>("/api/get-employee-directory", {
//     method: "POST",
//     payload: { page: currentPage, limit },
//     autoCall: true,
//     dependencies: [currentPage, limit],
//   });

//   useEffect(() => {
//     setTotalemp(employeeData?.data?.total);
//   }, []);

//   const transformedEmployees: TableEmployee[] =
//     employeeData?.data?.employees.map((emp, index) => ({
//       EmpId: emp.employee_id,
//       Name: emp.employee_name,
//       Email: emp.email_id,
//       Designation: emp.designation,
//       CurrentRole: emp.department,
//       Image: emp.photo,
//       WorkLocation: emp.work_location,
//       Status: emp.status,
//       Sno: index + 1,
//     })) ?? [];

//   const columns = [
//     {
//       title: "SNo.",
//       dataIndex: "Sno",
//     },
//     {
//       title: "Emp ID",
//       dataIndex: "EmpId",
//       render: (text: string, record: any) => (
//         <Link to={`/employee-details/${record.EmpId}`}>{text}</Link>
//       ),
//       sorter: (a: any, b: any) => a.EmpId.localeCompare(b.EmpId),
//     },
//     {
//       title: "Name",
//       dataIndex: "Name",
//       render: (text: string, record: any) => (
//         <div className="d-flex align-items-center">
//           <div
//             className="avatar avatar-md overflow-hidden rounded-circle"
//             style={{
//               width: "32px",
//               height: "32px",
//               minWidth: "32px",
//             }}
//           >
//             <ImageWithBasePath
//               src={
//                 record.Image ||
//                 "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQABqQIdskCD9BK0I81EbVfV9tTz320XvJ35A&s"
//               }
//               alt="img"
//               className="img-fluid w-100 h-100 object-fit-cover"
//               width={32}
//               height={32}
//             />
//           </div>
//           <div className="ms-2">
//             <p className="text-dark mb-0">{record.Name}</p>
//             <span className="fs-12">{record.CurrentRole}</span>
//           </div>
//         </div>
//       ),
//       sorter: (a: any, b: any) => a.Name.length - b.Name.length,
//     },
//     {
//       title: "Email",
//       dataIndex: "Email",
//       sorter: (a: any, b: any) => a.Email.length - b.Email.length,
//     },

//     {
//       title: "Designation",
//       dataIndex: "Designation",
//       render: (text: string, record: any) => (
//         <div className="dropdown me-3">{text}</div>
//       ),
//       sorter: (a: any, b: any) => a.Designation.length - b.Designation.length,
//     },
//     {
//       title: "Department",
//       dataIndex: "CurrentRole",
//       sorter: (a: any, b: any) => a.CurrentRole.localeCompare(b.CurrentRole),
//     },
//     {
//       title: "Work Location",
//       dataIndex: "WorkLocation",
//       sorter: (a: any, b: any) => a.WorkLocation.localeCompare(b.WorkLocation),
//     },
//     {
//       title: "Status",
//       dataIndex: "Status",
//       render: (text: string) => {
//         const isActive = text === "1" || text?.toLowerCase?.() === "1";
//         return (
//           <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
//             {isActive ? "Active" : "Exit"}
//           </span>
//         );
//       },
//       sorter: (a: any, b: any) =>
//         (a.Status || "").localeCompare(b.Status || ""),
//     },
//   ];
//   return (
//     <>
//       <Loader loading={summaryLoading || employeeLoading} />
//       {/* Page Wrapper */}
//       <div className="page-wrapper">
//         <div className="content">
//           {/* Breadcrumb */}
//           <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
//             <div className="my-auto mb-2">
//               <h2 className="mb-1">Employee Directory </h2>
//               <nav>
//                 <ol className="breadcrumb mb-0">
//                   <li className="breadcrumb-item">
//                     <Link to={all_routes.employeeDashboard}>
//                       <i className="ti ti-smart-home" />
//                     </Link>
//                   </li>
//                   <li className="breadcrumb-item">Employee Resources</li>
//                   <li className="breadcrumb-item active" aria-current="page">
//                     Employee Directory
//                   </li>
//                 </ol>
//               </nav>
//             </div>
//             <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
//               <div className="head-icons ms-2">
//                 <CollapseHeader />
//               </div>
//             </div>
//           </div>
//           {/* /Breadcrumb */}
//           {/* <div className="row">

//             <div className="col-lg-3 col-md-4 col-6 d-flex">
//               <div className="card flex-fill">
//                 <div className="card-body d-flex align-items-center justify-content-between">
//                   <div className="d-flex align-items-center overflow-hidden">
//                     <div>
//                       <span className="avatar avatar-lg bg-dark rounded-circle">
//                         <i className="ti ti-users" />
//                       </span>
//                     </div>
//                     <div className="ms-2 overflow-hidden">
//                       <p className="fs-12 fw-medium mb-1 text-truncate">
//                         Total Employees
//                       </p>
//                       <h4>{summaryData?.data?.total_employees ?? 0}</h4>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="col-lg-3 col-md-4 col-6 d-flex">
//               <div className="card flex-fill">
//                 <div className="card-body d-flex align-items-center justify-content-between">
//                   <div className="d-flex align-items-center overflow-hidden">
//                     <div>
//                       <span className="avatar avatar-lg bg-success rounded-circle">
//                         <i className="ti ti-user-share" />
//                       </span>
//                     </div>
//                     <div className="ms-2 overflow-hidden">
//                       <p className="fs-12 fw-medium mb-1 text-truncate">
//                         Active
//                       </p>
//                       <h4>{summaryData?.data?.active_employees ?? 0}</h4>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="col-lg-3 col-md-4 col-6 d-flex">
//               <div className="card flex-fill">
//                 <div className="card-body d-flex align-items-center justify-content-between">
//                   <div className="d-flex align-items-center overflow-hidden">
//                     <div>
//                       <span className="avatar avatar-lg bg-danger rounded-circle">
//                         <i className="ti ti-user-pause" />
//                       </span>
//                     </div>
//                     <div className="ms-2 overflow-hidden">
//                       <p className="fs-12 fw-medium mb-1 text-truncate">
//                         Inactive
//                       </p>
//                       <h4>{summaryData?.data?.inactive_employees ?? 0}</h4>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="col-lg-3 col-md-4 col-6 d-flex">
//               <div className="card flex-fill">
//                 <div className="card-body d-flex align-items-center justify-content-between">
//                   <div className="d-flex align-items-center overflow-hidden">
//                     <div>
//                       <span className="avatar avatar-lg bg-info rounded-circle">
//                         <i className="ti ti-user-plus" />
//                       </span>
//                     </div>
//                     <div className="ms-2 overflow-hidden">
//                       <p className="fs-12 fw-medium mb-1 text-truncate">
//                         New Joiners
//                       </p>
//                       <h4>{summaryData?.data?.new_joinings ?? 0}</h4>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div> */}

//           <div className="card">
//             <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
//               <h5>Employee List</h5>
//             </div>
//             <div className="card-body p-0">
//               {/* <Table
//                 dataSource={transformedEmployees}
//                 columns={columns}
//                 Selection={false}
//                 totalEmployees={employeeData?.data?.total}
//                 currentPage={currentPage}
//                 setCurrentPage={setCurrentPage}
//                 limit={limit}
//                 setLimit={setLimit}
//               /> */}
//               <Table
//                 dataSource={
//                   filteredEmployees.length > 0
//                     ? filteredEmployees
//                     : transformedEmployees
//                 }
//                 columns={columns}
//                 Selection={false}
//                 totalEmployees={
//                   filteredEmployees.length > 0
//                     ? totalemp ?? filteredEmployees.length
//                     : employeeData?.data?.total ?? 0
//                 }
//                 currentPage={currentPage}
//                 setCurrentPage={setCurrentPage}
//                 limit={limit}
//                 setLimit={setLimit}
//                 onSearch={handleSearch}
//                 searchText={searchText}
//               />
//             </div>
//           </div>
//         </div>
//         <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
//           <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
//         </div>
//       </div>
//       {/* /Page Wrapper */}
//     </>
//   );
// };

// export default EmployeeDirectory;
